import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectMfaToken, clearMfaPending } from "../../store/slices/authUserSlice";
import { store as reduxStore } from "../../store/store";
import { verifyMfaLogin, resendMfaLoginCode } from "../../services/mfaService";
import MfaModal from "./MfaModal";

const RESEND_COOLDOWN_SECONDS = 30;

const MfaVerification: React.FC = () => {
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendMessage, setResendMessage] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const mfaToken = useSelector(selectMfaToken);
  const navigate = useNavigate();

  useEffect(() => {
    if (!mfaToken) {
      navigate("/login");
    }
  }, [mfaToken, navigate]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!mfaToken || code.length !== 6) return;

    setIsVerifying(true);
    setError("");

    try {
      await verifyMfaLogin(mfaToken, code);
      navigate("/dashboard");
    } catch (err: any) {
      const message =
        err.response?.status === 429
          ? "Too many attempts. Please wait a few minutes."
          : err.response?.data?.error || "Verification failed. Please try again.";
      setError(message);
      setCode("");
      inputRef.current?.focus();
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResend = async () => {
    if (!mfaToken || resendCooldown > 0) return;

    try {
      await resendMfaLoginCode(mfaToken);
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
      setResendMessage("A new code has been sent to your phone.");
      setError("");
      setTimeout(() => setResendMessage(""), 5000);
    } catch (err: any) {
      if (err.response?.status === 429) {
        setError("Too many attempts. Please wait a few minutes.");
      } else {
        setError(err.response?.data?.error || "Failed to resend code.");
      }
    }
  };

  const handleBackToLogin = () => {
    reduxStore.dispatch(clearMfaPending());
    navigate("/login");
  };

  if (!mfaToken) return null;

  return (
    <div className="mfa-screen">
      <MfaModal>
        <h2 className="mfa-title">Two-Factor Authentication</h2>
        <p className="mfa-subtitle">
          We sent a verification code to your phone. Enter the 6-digit code below.
        </p>

        <form onSubmit={handleVerify} className="mfa-form">
          <div className="mfa-field">
            <label htmlFor="mfa-code" className="mfa-label">
              Code
            </label>
            <input
              ref={inputRef}
              id="mfa-code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              placeholder="000000"
              className="mfa-input mfa-input--code"
            />
          </div>

          {error && (
            <div className="mfa-alert mfa-alert--error">
              <p>{error}</p>
            </div>
          )}

          {resendMessage && (
            <div className="mfa-alert mfa-alert--success">
              <p>{resendMessage}</p>
            </div>
          )}

          <div className="mfa-inline-row">
            <button
              type="button"
              onClick={handleResend}
              disabled={resendCooldown > 0}
              className="mfa-link"
            >
              {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : "Resend code"}
            </button>
            <button
              type="button"
              onClick={handleBackToLogin}
              className="mfa-link"
            >
              Back to login
            </button>
          </div>

          <div className="mfa-actions">
            <button
              type="submit"
              disabled={isVerifying || code.length !== 6}
              className="mfa-btn mfa-btn--primary"
            >
              {isVerifying ? "Verifying..." : "Verify Code"}
            </button>
          </div>
        </form>
      </MfaModal>
    </div>
  );
};

export default MfaVerification;
